/**
 * Shared formatting helpers and option labels for the Personal Data Sheet,
 * used by both `PdsFormView.js` (on-screen) and `pdsPdfExport.js`.
 *
 * KEEP IN SYNC with frontend_pds/src/components/wizard/options.js and
 * .../AddressFields.js. The advisor SPA and the client wizard are separate
 * CRA apps with no shared package, so these definitions necessarily live in
 * two places. If they drift, the exported PDF will disagree with what the
 * client actually saw on screen.
 */

export const DASH = '—';

export const fmtText = (v) => {
  const t = String(v ?? '').trim();
  return t || DASH;
};

export const fmtYesNo = (v) => {
  if (v === 'yes' || v === true) return 'Yes';
  if (v === 'no' || v === false) return 'No';
  return DASH;
};

/** `<input type="date">` stores yyyy-mm-dd; people read mm/dd/yyyy. */
export const fmtDate = (v) => {
  const raw = String(v ?? '').trim();
  if (!raw) return DASH;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  return match ? `${match[2]}/${match[3]}/${match[1]}` : raw;
};

export const formatAddress = (a) => {
  if (!a || typeof a !== 'object') return DASH;
  const line1 = [a.street, a.street2].filter(Boolean).join(', ');
  const line2 = [a.city, [a.state, a.zip].filter(Boolean).join(' ')]
    .filter(Boolean).join(', ');
  const country = a.country && a.country !== 'USA' ? a.country : '';
  const joined = [line1, line2, country].filter(Boolean).join(', ');
  return joined || DASH;
};

export const EMPLOYMENT_STATUS_LABELS = {
  employed: 'Employed',
  self_employed: 'Self-employed',
  not_employed: 'Not employed',
  retired: 'Retired',
  homemaker: 'Homemaker',
  student: 'Student',
};

export const fmtEmploymentStatus = (v) => (
  EMPLOYMENT_STATUS_LABELS[String(v || '').toLowerCase()] || fmtText(v)
);

export const OCCUPATION_OTHER = 'Other (please specify)';

/** Resolve the occupation, expanding the free-text "Other" case. */
export const fmtOccupation = (employment = {}) => {
  if (employment.occupation === OCCUPATION_OTHER) {
    return fmtText(employment.occupation_other);
  }
  return fmtText(employment.occupation);
};

export const fmtCountry = (value, other) => {
  if (value === 'other') return fmtText(other);
  if (value === 'usa') return 'USA';
  return DASH;
};

/**
 * Contact-frequency preference. Cumulative options, so the labels read
 * "Option A + ..." exactly as the client saw them.
 */
export const COMMUNICATION_FREQUENCY_LABELS = {
  a: 'Option A: Quarterly newsletters, event invitations, and curated client articles',
  b: 'Option B: Option A + extra educational articles',
  c: 'Option C: Option B + anything else you feel is relevant to me',
};

export const fmtCommunicationFrequency = (v) => (
  COMMUNICATION_FREQUENCY_LABELS[String(v || '').toLowerCase()] || DASH
);

/** Employed and self-employed both name a business; only the wording differs. */
export const EMPLOYER_STATUSES = ['employed', 'self_employed'];

export const hasEmployerDetails = (employment = {}) => (
  EMPLOYER_STATUSES.includes(String(employment.status || '').toLowerCase())
);

export const employerLabels = (employment = {}) => (
  String(employment.status || '').toLowerCase() === 'self_employed'
    ? { name: 'Business name', address: 'Business address' }
    : { name: 'Employer name', address: 'Employer address' }
);

/** True once the optional secondary Trusted Contact has been started. */
export const secondaryTcpHasData = (tcp = {}) => {
  const s = tcp.secondary;
  if (!s || typeof s !== 'object') return false;
  return ['full_name', 'relationship', 'phone', 'email']
    .some((k) => String(s[k] ?? '').trim());
};

export const fmtDelivery = (v) => {
  if (v === 'yes') return 'Yes — electronically via client portal';
  if (v === 'no') return 'No — via postal mail';
  return DASH;
};

// ---------------------------------------------------------------------------
// Beneficiary percentages — integer hundredths, never floats. See the note in
// frontend_pds/src/components/wizard/percent.js.
// ---------------------------------------------------------------------------

export const toCents = (v) => {
  const n = parseFloat(String(v ?? '').replace('%', '').trim());
  return Number.isFinite(n) ? Math.round(n * 100) : null;
};

export const formatCents = (cents) => (Number(cents || 0) / 100).toFixed(2);

const addressHasData = (a) => (
  !!a && typeof a === 'object'
  && ['street', 'street2', 'city', 'state', 'zip']
    .some((k) => String(a[k] || '').trim())
);

export const beneficiaryHasData = (r) => {
  if (!r) return false;
  const scalar = ['pct', 'relationship', 'full_name', 'dob']
    .some((k) => String(r[k] ?? '').trim());
  return scalar || addressHasData(r.address);
};

export const filledBeneficiaries = (rows) => (
  (Array.isArray(rows) ? rows : []).filter(beneficiaryHasData)
);

export const sumCents = (rows) => (
  filledBeneficiaries(rows)
    .reduce((acc, r) => acc + (toCents(r.pct) ?? 0), 0)
);

/** Sections rendered for each person, in the order the client saw them. */
export const PERSON_SECTIONS = [
  { suffix: 'contact', title: 'Contact Information' },
  { suffix: 'employment', title: 'Employment' },
  { suffix: 'compliance', title: 'Compliance & Reporting' },
  { suffix: 'trusted_contact', title: 'Trusted Contact Person' },
  { suffix: 'beneficiaries', title: 'Beneficiaries' },
];

/** Prefix a section key for the spouse variant. */
export const sectionKey = (prefix, suffix) => (
  prefix ? `${prefix}_${suffix}` : suffix
);

export const householdHasSpouse = (formData) => (
  String(formData?.household?.has_spouse || '').toLowerCase() === 'yes'
);

// ---------------------------------------------------------------------------
// Filenames
// ---------------------------------------------------------------------------

const safeFilenameStem = (name) => (
  String(name || 'client').trim()
    .replace(/[^A-Za-z0-9 _.-]/g, '')
    .replace(/\s+/g, '_') || 'client'
);

const currentMonthYear = () => {
  const now = new Date();
  return `${String(now.getMonth() + 1).padStart(2, '0')}_${now.getFullYear()}`;
};

/** Must match `_default_summary_pdf_filename` on the backend. */
export const defaultPdsPdfFilename = (clientName) => (
  `${safeFilenameStem(clientName)}_Personal_Data_Sheet_${currentMonthYear()}.pdf`
);
